import React from 'react';
import { AlertCircle, AlertTriangle, CalendarCheck, Info } from 'lucide-react';

/**
 * Saldo urlopu wypoczynkowego dla wniosku (na wzór PanelLimitowNadgodzin).
 * `saldo` pochodzi z GET /kadry/wnioski-urlopowe/saldo (formularz) albo /:id/saldo (okno decyzji).
 * `tryb`: 'skladanie' (pracownik) albo 'decyzja' (przełożony — opis w trzeciej osobie).
 */

const dni = (n) => {
  const abs = Math.abs(n);
  if (abs === 1) return `${n} dzień`;
  return `${n} dni`;
};

const formatData = (ymd) => {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-');
  return `${d}.${m}.${y}`;
};

const Wiersz = ({ etykieta, wartosc, opis, mocny = false, klasa = '' }) => (
  <div className={`flex items-baseline justify-between gap-3 py-1 ${klasa}`}>
    <span className="text-xs text-gray-600">
      {etykieta}
      {opis && <span className="block text-[11px] text-gray-500">{opis}</span>}
    </span>
    <span className={`text-sm tabular-nums ${mocny ? 'font-bold' : 'font-semibold'}`}>{wartosc}</span>
  </div>
);

const SaldoRoku = ({ s, decyzja, jedenRok }) => {
  const wykorzystaneRazem = s.wykorzystane + s.bilansWejsciowy + s.korekta;
  const opisWykorzystanych = [
    s.bilansWejsciowy ? `w tym ${dni(s.bilansWejsciowy)} sprzed systemu` : null,
    s.korekta ? `korekta HR ${s.korekta > 0 ? '+' : ''}${s.korekta}` : null
  ].filter(Boolean).join(', ');
  const kolorWyniku = s.poZatwierdzeniu < 0 ? 'text-red-700' : s.poZatwierdzeniu <= 2 ? 'text-amber-700' : 'text-emerald-700';
  const pokazNaZadanie = s.naZadanie.tenWniosek > 0 || s.naZadanie.wykorzystane > 0;

  return (
    <div className={jedenRok ? '' : 'rounded-lg border border-black/[0.06] bg-white/60 px-3 py-2'}>
      {!jedenRok && <p className="text-xs font-semibold text-gray-700 mb-1">Rok {s.rok}{s.tenWniosek ? ` — ${dni(s.tenWniosek)} z tego wniosku` : ''}</p>}
      <div className="divide-y divide-black/[0.05]">
        <Wiersz
          etykieta={`Przysługuje w ${s.rok}`}
          wartosc={dni(s.dostepneOgolem)}
          opis={s.zalegly > 0 ? `wymiar ${s.wymiar} + zaległy ${s.zalegly}` : `wymiar roczny`}
        />
        <Wiersz etykieta="Wykorzystane" wartosc={dni(wykorzystaneRazem)} opis={opisWykorzystanych || null} />
        <Wiersz etykieta="Zaplanowane (zatwierdzone wnioski)" wartosc={dni(s.zaplanowane)} />
        <Wiersz etykieta={decyzja ? 'Inne wnioski oczekujące' : 'Wnioski oczekujące na decyzję'} wartosc={dni(s.oczekujace)} />
        <Wiersz etykieta="Do wykorzystania teraz" wartosc={dni(s.pozostaloTeraz)} mocny />
        <Wiersz etykieta="Ten wniosek" wartosc={s.tenWniosek ? `− ${dni(s.tenWniosek)}` : '—'} />
        <Wiersz
          etykieta={decyzja ? 'Zostanie po zatwierdzeniu' : 'Zostanie po zatwierdzeniu wniosku'}
          wartosc={dni(s.poZatwierdzeniu)}
          mocny
          klasa={kolorWyniku}
        />
      </div>
      {s.zaleglyPozostaly > 0 && (
        <p className="mt-1.5 text-[11px] text-amber-800">
          W tym zaległy urlop: {dni(s.zaleglyPozostaly)} do wykorzystania do {formatData(s.terminZaleglego)} (art. 168 KP).
        </p>
      )}
      {pokazNaZadanie && (
        <p className={`mt-1.5 text-[11px] ${s.naZadanie.pozostalo < 0 ? 'text-red-700 font-medium' : 'text-gray-600'}`}>
          Urlop na żądanie: wykorzystane {s.naZadanie.wykorzystane}
          {s.naZadanie.tenWniosek ? ` + ${s.naZadanie.tenWniosek} z tego wniosku` : ''} z {s.naZadanie.limit} dni w roku
          {s.naZadanie.pozostalo >= 0 ? ` — zostanie ${s.naZadanie.pozostalo}` : ''}.
        </p>
      )}
    </div>
  );
};

const PanelSaldaUrlopu = ({ saldo, tryb = 'skladanie', ladowanie = false, blad = false }) => {
  if (ladowanie && !saldo) {
    return <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500">Liczenie salda urlopu…</div>;
  }
  if (blad && !saldo) {
    return <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500">Nie udało się pobrać salda urlopu.</div>;
  }
  if (!saldo) return null;

  const decyzja = tryb === 'decyzja';
  const przekroczenie = saldo.przekroczenie;
  const ostrzezenieNaZadanie = saldo.przekroczenieNaZadanie;
  const klasaRamki = przekroczenie
    ? 'border-red-200 bg-red-50 text-red-900'
    : ostrzezenieNaZadanie
      ? 'border-amber-200 bg-amber-50 text-amber-900'
      : 'border-emerald-200 bg-emerald-50/60 text-gray-900';
  const jedenRok = saldo.lata.length === 1;

  return (
    <div className={`rounded-xl border px-4 py-3 ${klasaRamki} ${ladowanie ? 'opacity-70' : ''}`}>
      <p className="text-sm font-medium flex items-center gap-1.5 mb-1">
        <CalendarCheck className="h-4 w-4 shrink-0" />
        {decyzja ? 'Saldo urlopu pracownika' : 'Twój urlop wypoczynkowy'}
        {jedenRok ? ` ${saldo.lata[0].rok}` : ''}
      </p>

      {saldo.liczonyZPuli ? (
        <div className={jedenRok ? '' : 'grid gap-2 sm:grid-cols-2'}>
          {saldo.lata.map((s) => <SaldoRoku key={s.rok} s={s} decyzja={decyzja} jedenRok={jedenRok} />)}
        </div>
      ) : (
        <p className="text-xs text-gray-600">
          Do wykorzystania z urlopu wypoczynkowego: <strong>{dni(saldo.lata[0].pozostaloTeraz)}</strong>.
        </p>
      )}

      {przekroczenie && (
        <div className="mt-2 text-xs font-medium space-y-0.5">
          {saldo.ostrzezenia.map((o) => (
            <p key={o} className="flex items-start gap-1"><AlertCircle className="h-3.5 w-3.5 shrink-0 mt-px" />{o}</p>
          ))}
          <p className="font-normal">
            {decyzja
              ? 'Zatwierdzenie jest możliwe, ale pracownik przekroczy przysługujący urlop — w razie wątpliwości skontaktuj się z HR.'
              : 'Wniosek można złożyć, ale przełożony zobaczy, że przekracza on dostępny urlop.'}
          </p>
        </div>
      )}
      {!przekroczenie && saldo.ostrzezenia.length > 0 && (
        <div className="mt-2 text-xs space-y-0.5">
          {saldo.ostrzezenia.map((o) => (
            <p key={o} className="flex items-start gap-1"><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />{o}</p>
          ))}
        </div>
      )}
      {saldo.uwagi.length > 0 && (
        <div className="mt-2 text-[11px] text-gray-600 space-y-0.5">
          {saldo.uwagi.map((u) => (
            <p key={u} className="flex items-start gap-1"><Info className="h-3 w-3 shrink-0 mt-px" />{u}</p>
          ))}
        </div>
      )}
    </div>
  );
};

export default PanelSaldaUrlopu;
